import {
  Box,
  Button,
  Input,
  Menu,
  MenuButton,
  MenuList,
  Text,
  HStack,
  MenuItem
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AffiliationCountryList,
  fetchAffiliationCountryList
} from 'src/api/queries';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { useQuery } from '@tanstack/react-query';

type Props = {
  value: AffiliationCountryList | null;
  onChange: (country: AffiliationCountryList) => void;
  isInvalid?: boolean;
};

export const CountrySearchSelect: React.FC<Props> = ({
  value,
  onChange,
  isInvalid
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const { data: countriesData } = useQuery({
    queryKey: ['affiliation-country-list'],
    queryFn: () => fetchAffiliationCountryList()
  });
  const countries = countriesData?.items || [];
  const filtered = useMemo(() => {
    return (
      countries?.filter(c =>
        c.country.toLowerCase().includes(search.toLowerCase())
      ) ?? []
    );
  }, [countries, search]);
  useEffect(() => {
    if (buttonRef.current) {
      const { width } = buttonRef.current.getBoundingClientRect();
      setMenuWidth(width);
    }
  }, []);
  return (
    <Menu isOpen={menuOpen} onClose={() => setMenuOpen(false)}>
      <MenuButton
        as={Button}
        aria-label={
          value?.country
            ? `Selected country ${value.country}`
            : 'Select country'
        }
        rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
        onClick={() => setMenuOpen(!menuOpen)}
        width="100%"
        textAlign="left"
        backgroundColor={isInvalid ? 'errorPrimaryFill' : 'surfacePrimaryFill'}
        border={`1px solid`}
        _focus={{ boxShadow: 'outline', bg: 'surfacePrimaryFill' }}
        _hover={{ bg: 'surfacePrimaryFill' }}
        cursor="pointer"
        ref={buttonRef}
        height="auto"
        py={2}
        px={2}
        borderColor={isInvalid ? 'errorPrimaryBorder' : ''}
      >
        <HStack>
          <Text textStyle="textContext">{value?.countryCode ?? ''}</Text>
          <Text textStyle="textContext" fontSize="lg">
            {value?.country ?? 'Select country'}
          </Text>
        </HStack>
      </MenuButton>
      <MenuList
        pt={0}
        maxH="300px"
        overflowY="auto"
        width={menuWidth ? `${menuWidth}px` : 'auto'}
      >
        <Box
          position="sticky"
          top="0"
          bg="surfacePrimaryFill"
          zIndex="1"
          p={2}
          borderBottom="1px solid #e2e8f0"
        >
          <Input
            placeholder="Search country"
            size="sm"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="country-search"
          />
        </Box>
        {filtered.length > 0 ? (
          filtered.map(country => (
            <MenuItem
              key={country.countryCode}
              onClick={() => {
                onChange(country);
                setMenuOpen(false);
                setSearch('');
              }}
            >
              <HStack py={1}>
                <Text textStyle="textContext">{country.countryCode}</Text>
                <Text textStyle="textContext" fontSize="lg">
                  {country.country}
                </Text>
              </HStack>
            </MenuItem>
          ))
        ) : (
          <Box p={2}>
            <Text>No countries found</Text>
          </Box>
        )}
      </MenuList>
    </Menu>
  );
};
